import { navigateTo } from "@shared/utils.routing";
import { useLocale, useUserV2 } from "hooks";
import {
  addBasketItemsWithSpinner,
  addWishlistItemBySKU,
  removeWishlistItemBySKU
} from "lib/api";
import { useCallback } from "react";
import { toProductCardData as originalToProductCardData } from "@shared/ui.components";
import { toVariantType } from "packages/core-shopping/shared/toVariantType";
import type {
  MultipleProductCard,
  SingularProductCard,
  VariantType
} from "@shared/graphql.types";
import { Logger } from "lib/logger";
import { useVariantIds } from "hooks/useVariantIds";
import { useProductCardTracking } from "./useProductCardTracking";
import type { ProductCardTrackingPayload } from "../types";
import type { CtaButtonAction } from "../HorizontalProductCardWrapper";

// Type assertion to handle the updated function signature
const toProductCardData = originalToProductCardData as (
  productCard: MultipleProductCard | SingularProductCard,
  activeShade?: number,
  inWishlist?: boolean,
  hasMembershipInBag?: boolean,
  ctaButtonAction?: CtaButtonAction
) => ReturnType<typeof originalToProductCardData>;

const giftMembershipIdstoSkus = {
  "3074457345616768734": "BP_PLUS_GIFT_UK",
  "3074457345616760245": "BP_PLUS_GIFT_US"
};

interface ProductCardContext {
  location: string;
  activeShade?: number;
  productClickedTrackingPayload?: ProductCardTrackingPayload;
  // For add-to-basket behavior
  addToBasketOptions: {
    afterAdd?: (variant: VariantType) => void;
    beforeAdd?: () => void;
  };
  ctaButtonAction: CtaButtonAction;
}

export const useProductCardHandlers = (
  product: SingularProductCard | MultipleProductCard,
  context: ProductCardContext
) => {
  const { wishlist, isLoggedIn, hasMembershipInBag } = useUserV2();
  const { activeShade = 0 } = context;
  const inWishlist = wishlist?.some(
    (item) => item.variant?.sku === product.variants[0].sku
  );
  const productCardData = toProductCardData(
    product,
    activeShade,
    inWishlist,
    hasMembershipInBag,
    context.ctaButtonAction
  );
  const { trackProductClick } = useProductCardTracking(productCardData, {
    location: context.location,
    ...context.productClickedTrackingPayload
  });
  const locale = useLocale();
  const { giftMembershipProductId } = useVariantIds();
  const variant = product.variants[activeShade];

  const giftMembershipSku = giftMembershipIdstoSkus[giftMembershipProductId];
  const isGiftMembership = giftMembershipSku === variant.sku;

  const { brandName = "", productName = "" } = productCardData;
  const locationTypeDetails = `${productName} ${brandName} ${
    variant?.shade ? variant?.shade : ""
  }`;

  const onWishlistToggle = useCallback(() => {
    if (!isLoggedIn) {
      return navigateTo("/login");
    }

    if (productCardData.inWishlist) {
      return removeWishlistItemBySKU(productCardData.sku);
    }

    return addWishlistItemBySKU(productCardData.sku);
  }, [isLoggedIn, productCardData]);

  const handleAddToBasket = async () => {
    if (isGiftMembership) {
      await addBasketItemsWithSpinner(
        [
          {
            quantity: 1,
            variantId: giftMembershipProductId,
            variantSku:
              locale === "en-US" ? "BP_PLUS_GIFT_US" : "BP_PLUS_GIFT_UK",
            type: "product",
            origin: "user"
          }
        ],
        context.location,
        locationTypeDetails
      )
        .then(() => navigateTo("/basket"))
        .catch(() => Logger.error(`Error adding membership to basket`));
      return;
    }

    const items = [
      {
        quantity: 1,
        type: "product" as const,
        origin: "user" as const,
        variantSku: productCardData.sku
      }
    ];

    context.addToBasketOptions?.beforeAdd?.();

    await addBasketItemsWithSpinner(
      items,
      context.location,
      locationTypeDetails
    );

    const variantType = toVariantType(variant, product);

    context.addToBasketOptions.afterAdd?.(variantType);
  };

  return {
    onButtonClick: handleAddToBasket,
    onWishlistToggle: onWishlistToggle,
    onCardClick: trackProductClick,
    productCardData,
    locationTypeDetails,
    activeVariant: variant
  };
};
